import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { WhatsAppModal } from './components/WhatsAppModal';
import { FeedbackModal } from './components/FeedbackModal';
import { PolicyModal, PolicyType } from './components/PolicyModal';
import { AdminLoginModal } from './components/AdminLoginModal';
import { ArrearCalculatorModal } from './components/ArrearCalculatorModal';
import { AdminDashboard } from './components/AdminDashboard';
import { HomeView } from './components/HomeView';
import { ResourceView } from './components/ResourceView';
import { ExclusiveView } from './components/ExclusiveView';
import { SalaryCalculatorView } from './components/SalaryCalculatorView';
import { TdsFormView } from './components/TdsFormView';
import { BRCView } from './components/BRCView';
import { AssetLiabilityView } from './components/AssetLiabilityView';
import { AbsentyView } from './components/AbsentyView';
import { ArrearAutomaticView } from './components/ArrearAutomaticView';
import { ArrearSelectionView } from './components/ArrearSelectionView';
import { SchoolPage } from './components/SchoolPage';
import { SearchSchoolView } from './components/SearchSchoolView';
import { DataManagerView } from './components/DataManagerView';
import { db, doc, getDoc, setDoc, onSnapshot, handleFirestoreError, OperationType, increment, runTransaction } from './src/firebase';
import { TabType, TreData, NavbarSettings } from './types';
import { TRE_DATA } from './constants';
import { LogOut, LayoutDashboard, Heart, Calculator, ArrowRight, Banknote, Clock, FileSearch } from 'lucide-react';
import { saveUsageData } from './services/usageService';

const App = () => {
  const [activeTab, setActiveTab] = useState<TabType | 'admin'>('home');
  const [navbarSettings, setNavbarSettings] = useState<NavbarSettings>({});

  const handleTabChange = (tab: TabType | 'admin') => {
    if (tab === activeTab) {
      window.scrollTo(0, 0);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    // Track visitor
    const trackVisitor = async () => {
      try {
        const statsRef = doc(db, 'stats', 'visitors');
        const today = new Date().toISOString().split('T')[0];
        
        await runTransaction(db, async (transaction) => {
          const statsSnap = await transaction.get(statsRef);
          
          if (statsSnap.exists()) {
            const data = statsSnap.data();
            const lastUpdate = data.lastUpdate || '';
            const todayCount = lastUpdate === today ? (data.today || 0) + 1 : 1;
            
            transaction.update(statsRef, {
              total: increment(1),
              today: todayCount,
              lastUpdate: today
            });
          } else {
            transaction.set(statsRef, {
              total: 1,
              today: 1,
              lastUpdate: today
            });
          }
        });
      } catch (error) {
        console.error('Error tracking visitor:', error);
      }
    };
    trackVisitor();
  }, []);
  
  useEffect(() => {
    if (activeTab !== 'admin' && activeTab !== 'home') {
      saveUsageData({
        section: `Viewed: ${activeTab.toUpperCase()}`,
      }).catch(console.error);
    }
    window.scrollTo(0, 0);
  }, [activeTab]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [activePolicy, setActivePolicy] = useState<PolicyType>('privacy');
  const [isArrearModalOpen, setIsArrearModalOpen] = useState(false);
  const [isAutomaticArrear, setIsAutomaticArrear] = useState(false);
  const [isExperiment, setIsExperiment] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [resourceData, setResourceData] = useState<TreData>(TRE_DATA);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'resources'), (snapshot) => {
      if (snapshot.exists()) {
        setResourceData(snapshot.data() as TreData);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'settings/resources');
    });

    const unsubNavbar = onSnapshot(doc(db, 'settings', 'navbar'), (snapshot) => {
      if (snapshot.exists()) {
        setNavbarSettings(snapshot.data() as NavbarSettings);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'settings/navbar');
    });

    return () => {
      unsub();
      unsubNavbar();
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsWhatsAppOpen(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleBannerClick = (isAuto: boolean = false, isExp: boolean = false) => {
    setIsAutomaticArrear(isAuto);
    setIsExperiment(isExp);
    setIsArrearModalOpen(true);
  };

  const renderContent = () => {
    if (activeTab === 'admin') {
      return isAdminLoggedIn 
        ? <AdminDashboard data={resourceData} onUpdate={setResourceData} navbarSettings={navbarSettings} onUpdateNavbarSettings={setNavbarSettings} /> 
        : <HomeView setTab={handleTabChange as any} />;
    }
    switch (activeTab) {
      case 'home': return <HomeView setTab={handleTabChange as any} />;
      case 'salary': return <SalaryCalculatorView />;
      case 'tre2': return <TdsFormView />;
      case 'brc': return <BRCView />;
      case 'absenty': return <AbsentyView />;
      case 'school': return <SchoolPage />;
      case 'searchSchool': return <SearchSchoolView />;
      case 'dataManager': return <DataManagerView />;
      case 'arrear': return <ArrearSelectionView onSelect={handleBannerClick} />;
      case 'tre1': return <ResourceView data={resourceData.tre1} setTab={handleTabChange as any} onBannerClick={() => handleBannerClick(false)} />;
      case 'tre3': return <ResourceView data={resourceData.tre3} setTab={handleTabChange as any} />;
      case 'bpscTre3': return <AssetLiabilityView />;
      case 'automaticArrear': return <ArrearAutomaticView />;
      case 'autoArrearExperiment': return <ResourceView data={resourceData.autoArrearExperiment} setTab={handleTabChange as any} onBannerClick={() => handleBannerClick(true, true)} />;
      case 'exclusive': return <ExclusiveView />;
      default: return <HomeView setTab={handleTabChange as any} />;
    }
  };

  const handlePolicyClick = (type: PolicyType) => {
    setActivePolicy(type);
    setIsPolicyOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans selection:bg-orange-100 selection:text-orange-900">
      <Navbar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isAdminLoggedIn={isAdminLoggedIn}
        setShowLoginModal={setShowLoginModal}
        setIsAdminLoggedIn={setIsAdminLoggedIn}
        navbarSettings={navbarSettings}
      />

      {isAdminLoggedIn && (
        <div className="bg-gray-900 text-white px-4 py-3 flex justify-between items-center text-sm sticky top-20 z-[49] shadow-xl border-b border-gray-800">
          <span className="font-semibold flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            Admin Active
          </span>
          <div className="flex space-x-6">
             <button onClick={() => setActiveTab('admin')} className={`hover:text-orange-300 transition flex items-center ${activeTab === 'admin' ? 'text-orange-400 font-bold' : ''}`}>
               <LayoutDashboard size={16} className="mr-1.5" /> Dashboard
             </button>
             <button onClick={() => setIsAdminLoggedIn(false)} className="hover:text-red-300 transition flex items-center">
               <LogOut size={16} className="mr-1.5" /> Logout
             </button>
          </div>
        </div>
      )}

      <main className="flex-grow">{renderContent()}</main>

      <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">About BR TEACHER</h4>
              <p className="text-sm leading-relaxed">
                Bihar's premier resource portal for the teaching community. Providing automated calculators, salary slips extraction, and TRE updates.
              </p>
              <button onClick={() => handlePolicyClick('about')} className="text-orange-500 text-xs font-bold mt-2 hover:underline">Read More About Us →</button>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => handleTabChange('arrear')} className="hover:text-orange-500">Arrear Calculator</button></li>
                <li><button onClick={() => handleTabChange('tre2')} className="hover:text-orange-500">TDS & Tax</button></li>
                <li><button onClick={() => handleTabChange('salary')} className="hover:text-orange-500">Salary Check</button></li>
                <li><button onClick={() => handleTabChange('absenty')} className="hover:text-orange-500">Absenty Report</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact & Support</h4>
              <p className="text-sm mb-1">Business: <span className="text-white font-bold">BRTEACHER</span></p>
              <p className="text-xs mb-3 text-gray-500">Rajendra Nagar, Gopalganj, Bihar</p>
              <div className="space-y-1 mb-4">
                <p className="text-xs">Email: <a href="mailto:Support@brteacher.in" className="text-orange-500 hover:underline">Support@brteacher.in</a></p>
                <p className="text-xs">Phone: <a href="tel:9229001900" className="text-gray-300 hover:text-white">+91 9229001900</a></p>
              </div>
              <div className="flex flex-col space-y-2">
                <button onClick={() => handlePolicyClick('contact')} className="text-xs bg-orange-600/10 text-orange-500 border border-orange-600/20 hover:bg-orange-600/20 px-4 py-2 rounded-lg transition text-left font-bold">
                  Contact Us Page
                </button>
                <button onClick={() => setIsFeedbackOpen(true)} className="text-xs bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition text-left">
                  Send Feedback / Report Issue
                </button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center md:text-left flex flex-col md:md:flex-row justify-between items-center text-xs text-gray-600">
             <p>&copy; 2024 BR Teacher (All rights reserved.)</p>
             <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 mt-4 md:mt-0">
               <button onClick={() => handlePolicyClick('about')} className="hover:text-orange-500 transition font-bold">About Us</button>
               <button onClick={() => handlePolicyClick('contact')} className="hover:text-orange-500 transition font-bold">Contact Us</button>
               <button onClick={() => handlePolicyClick('privacy')} className="hover:text-orange-500 transition font-bold">Privacy Policy</button>
               <button onClick={() => handlePolicyClick('terms')} className="hover:text-orange-500 transition font-bold">Terms & Conditions</button>
               <button onClick={() => handlePolicyClick('refund')} className="hover:text-orange-500 transition font-bold">Refund Policy</button>
             </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <WhatsAppModal isOpen={isWhatsAppOpen} onClose={() => setIsWhatsAppOpen(false)} />
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      <PolicyModal isOpen={isPolicyOpen} onClose={() => setIsPolicyOpen(false)} type={activePolicy} />
      <AdminLoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLogin={(status) => { setIsAdminLoggedIn(status); if(status) handleTabChange('admin'); }} />
      <ArrearCalculatorModal 
        isOpen={isArrearModalOpen} 
        onClose={() => setIsArrearModalOpen(false)} 
        isAutomatic={isAutomaticArrear} 
        isExperiment={isExperiment}
      />
    </div>
  );
};

export default App;